/**
 * Яндекс.Метрика.
 *
 * Сам счётчик подключается в `index.html` — там он успевает инициализироваться
 * до того, как React отрисует первый кадр, и считает первый просмотр сам.
 * Этот модуль нужен для всего остального: clapme — SPA, адресная строка меняется
 * без перезагрузки, и переходы `/` → `/admin/:code` → `/present/:code` Метрика
 * без явного `hit` просто не увидит.
 *
 * Все вызовы безопасны, когда счётчика нет: в деве и в тестах его не грузят
 * (см. условие в `index.html`), и `window.ym` там не определён.
 */

/** Номер счётчика; тот же, что в сниппете в `index.html`. */
export const COUNTER_ID = 112829818

declare global {
  interface Window {
    ym?: (counter: number, action: string, ...args: unknown[]) => void
  }
}

function send(action: string, ...args: unknown[]) {
  // Счётчик асинхронный, но очередь `ym` появляется синхронно вместе со
  // сниппетом — так что проверка ловит именно «Метрику не подключали»,
  // а не «не успела загрузиться».
  window.ym?.(COUNTER_ID, action, ...args)
}

/**
 * Просмотр страницы при переходе внутри SPA.
 *
 * `referer` передаём руками: браузер при `history.pushState` его не меняет,
 * и без этого в отчётах все внутренние переходы выглядели бы как заходы
 * с того же источника, что и первый.
 */
export function trackPageView(url: string, referer?: string) {
  send('hit', url, referer ? { referer } : undefined)
}

/** Достижение цели. Имя должно совпадать с идентификатором цели в интерфейсе Метрики. */
export function reachGoal(name: string, params?: Record<string, unknown>) {
  send('reachGoal', name, params)
}

/**
 * Цели, которые размечаем в коде. Держим списком, чтобы имена в интерфейсе
 * Метрики и в вызовах не разъезжались.
 */
export const GOAL = {
  /** Ведущий создал эфир на главной. */
  sessionCreated: 'session_created',
  /** Участник вошёл в эфир по коду или QR. */
  sessionJoined: 'session_joined',
  /** Участник впервые ответил в этом эфире: опрос, свободный ответ или реакция. */
  participantAnswered: 'participant_answered',
  /** Клик по кнопке подключения тарифа на витрине — старт воронки покупки. */
  purchaseClick: 'purchase_click',
  /** Клик по «Войти через Яндекс» — старт входа в аккаунт. */
  loginClick: 'login_click',
} as const
